import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { SpendingLevel, SpendingTier, TotalSpendingsProps } from "./types";

// Default spending ladder: Starter → Advanced → Expert, matching the real
// profile levels (see components/TotalSpendings getCurrentLevel). Axis renders
// $ 0 / $ 2 000 / $ 8 000 / ∞; the last tier's `max` only shapes its fill.
// The custom/special profile renders the single full-width bar instead
// (`special` prop).
const DEFAULT_TIERS: SpendingTier[] = [
  { level: "starter", label: "Starter", min: 0, max: 2000 },
  { level: "advanced", label: "Advanced", min: 2000, max: 8000 },
  { level: "expert", label: "Expert", min: 8000, max: 20000 },
];

const TONE: Record<SpendingLevel, string> = {
  starter: styles["tone-starter"],
  advanced: styles["tone-advanced"],
  expert: styles["tone-expert"],
  special: styles["tone-special"],
};

// "$ 2 000" — space-separated thousands, matching the Figma axis labels.
const money = (n: number) =>
  `$ ${Math.round(n).toLocaleString("en-US").replace(/,/g, " ")}`;

// Fill % of a single segment; a just-started segment keeps a 2% sliver visible.
const fillPercent = (amount: number, min: number, max: number) => {
  if (amount <= min) return 0;
  if (amount >= max) return 100;
  return Math.max(((amount - min) / (max - min)) * 100, 2);
};

// The single full-width "Special" progress row.
const SpecialBar: React.FC = () => (
  <div className={cs(styles.tariff, TONE.special)}>
    <div className={styles.labels}>
      <span className={cs(styles.tag, styles.active)}>
        <span className={styles.dot} aria-hidden />
        Special
      </span>
    </div>
    <div className={styles.track}>
      <span className={styles.segment}>
        <span className={styles.fill} style={{ width: "100%" }} />
      </span>
    </div>
    <div className={styles.axis}>
      <span className={styles.axisStart}>{money(0)}</span>
      <span className={styles.axisMid} aria-hidden />
      <span className={styles.axisEnd}>∞</span>
    </div>
  </div>
);

// The segmented Starter / Advanced / Expert progress row.
const SegmentedBar: React.FC<{ amount: number; tiers: SpendingTier[] }> = ({
  amount,
  tiers,
}) => {
  const activeIndex = tiers.findIndex((t) => amount < t.max);
  const current = activeIndex === -1 ? tiers.length - 1 : activeIndex;

  return (
    <div className={cs(styles.tariff, TONE[tiers[current].level])}>
      <div className={styles.labels}>
        {tiers.map((t, i) => (
          <span
            key={t.label}
            className={cs(styles.tag, { [styles.active]: i === current })}
          >
            <span className={styles.dot} aria-hidden />
            {t.label}
          </span>
        ))}
      </div>
      <div className={styles.track}>
        {tiers.map((t) => (
          <span key={t.label} className={styles.segment}>
            <span
              className={styles.fill}
              style={{ width: `${fillPercent(amount, t.min, t.max)}%` }}
            />
          </span>
        ))}
      </div>
      <div className={styles.axis}>
        <span className={styles.axisStart}>{money(tiers[0].min)}</span>
        {tiers.slice(0, -1).map((t) => (
          <span key={t.label} className={styles.axisMid}>
            {money(t.max)}
          </span>
        ))}
        <span className={styles.axisEnd}>∞</span>
      </div>
    </div>
  );
};

/**
 * Total-spending card (Figma 568:75372). Pure presentational: caption + amount
 * + a segmented spending-progress bar whose active level and per-segment fill
 * are derived from `amount` + `tiers`. White card surface flips in dark mode.
 */
export const TotalSpendings: React.FC<TotalSpendingsProps> = ({
  amount,
  tiers = DEFAULT_TIERS,
  special = false,
  className,
}) => {
  // "1,620" + ".25" — comma thousands in the int part, matching Figma.
  const [rawInt, decPart] = Math.abs(amount).toFixed(2).split(".");
  const intPart = Number(rawInt).toLocaleString("en-US");

  return (
    <div className={cs(styles.card, className)}>
      <div className={styles.head}>
        <p className={styles.caption}>Total spending:</p>
        <p className={styles.amount}>
          <span className={styles.currency}>$</span>
          <span className={styles.int}>{intPart}</span>
          <span className={styles.decimal}>.{decPart}</span>
        </p>
      </div>
      {special ? (
        <SpecialBar />
      ) : (
        <SegmentedBar amount={Math.abs(amount)} tiers={tiers} />
      )}
    </div>
  );
};

export default TotalSpendings;
